import { useLocation, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, ArrowLeft, Clock, Hammer, Package, Sparkles } from "lucide-react";

const ResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { estimate, projectInfo } = location.state || {};

  useEffect(() => {
    if (!estimate) {
      navigate('/');
    }
  }, [estimate, navigate]);

  if (!estimate) {
    return null;
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatSize = (size) => {
    const sizes = {
      small: "Small (up to 15m²)",
      medium: "Medium (15-30m²)",
      large: "Large (30-50m²)",
      xl: "Extra Large (50m²+)"
    };
    return sizes[size] || size;
  };

  const formatQuality = (quality) => {
    const qualities = {
      budget: "Budget",
      standard: "Standard",
      premium: "Premium"
    };
    return qualities[quality] || quality;
  };

  const formatComplexity = (complexity) => {
    const complexities = {
      simple: "Simple",
      moderate: "Moderate",
      complex: "Complex"
    };
    return complexities[complexity] || complexity;
  };

  const handleRequestQuote = () => {
    navigate('/request-quote', { state: { estimate, projectInfo } });
  };

  const materialsPercent = Math.round((estimate.materials_cost / estimate.total_estimate) * 100);
  const labourPercent = Math.round((estimate.labour_cost / estimate.total_estimate) * 100);
  const additionalPercent = estimate.additional_cost > 0 
    ? Math.round((estimate.additional_cost / estimate.total_estimate) * 100) 
    : 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Your Estimate | TJP Carpentry & Construction</title>
      </Helmet>
      <Header />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="text-center mb-12 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
            <Sparkles className="text-green-600" size={32} />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-2">
            Your Estimate is Ready
          </h1>
          <p className="text-stone-600">
            Based on your selections for {projectInfo?.name}
          </p>
        </div>

        {/* Total Estimate Card */}
        <div className="bg-slate-900 text-white rounded-lg p-8 mb-8 animate-fade-in-up animation-delay-100" data-testid="total-estimate-card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-stone-400 text-sm font-medium mb-1">Estimated Total Cost</p>
              <p className="text-4xl md:text-5xl font-black">{formatCurrency(estimate.total_estimate)}</p>
            </div>
            <div className="flex items-center gap-3 bg-white/10 rounded-lg px-6 py-4">
              <Clock className="text-amber-500" size={24} />
              <div>
                <p className="text-stone-400 text-xs">Estimated Timeline</p>
                <p className="text-xl font-bold">{estimate.timeline_weeks} weeks</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="bg-white rounded-lg shadow-xl shadow-stone-200/50 p-8 mb-8 animate-fade-in-up animation-delay-200">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Cost Breakdown</h2>
          
          <div className="space-y-6">
            {/* Materials */}
            <div data-testid="materials-breakdown">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Package className="text-blue-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Materials</p>
                    <p className="text-sm text-stone-500">{materialsPercent}% of total</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(estimate.materials_cost)}</p>
              </div>
              <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                <div 
                  className="cost-bar h-full bg-blue-500 rounded-full" 
                  style={{ width: `${materialsPercent}%` }}
                />
              </div>
            </div>

            {/* Labour */}
            <div data-testid="labour-breakdown">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Hammer className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Labour</p>
                    <p className="text-sm text-stone-500">{labourPercent}% of total</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-slate-900">{formatCurrency(estimate.labour_cost)}</p>
              </div>
              <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                <div 
                  className="cost-bar h-full bg-amber-500 rounded-full" 
                  style={{ width: `${labourPercent}%` }}
                />
              </div>
            </div>

            {/* Additional Features */}
            {estimate.additional_cost > 0 && (
              <div data-testid="additional-breakdown">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Sparkles className="text-green-600" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Additional Features</p>
                      <p className="text-sm text-stone-500">{additionalPercent}% of total</p>
                    </div>
                  </div>
                  <p className="text-xl font-bold text-slate-900">{formatCurrency(estimate.additional_cost)}</p>
                </div>
                <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                  <div 
                    className="cost-bar h-full bg-green-500 rounded-full" 
                    style={{ width: `${additionalPercent}%` }}
                  />
                </div>
                
                {/* Features Detail */}
                {Object.keys(estimate.breakdown.features_breakdown).length > 0 && (
                  <div className="mt-4 pl-13 space-y-2">
                    {Object.entries(estimate.breakdown.features_breakdown).map(([feature, cost]) => (
                      <div key={feature} className="flex justify-between text-sm">
                        <span className="text-stone-600 capitalize">{feature.replace(/_/g, ' ')}</span>
                        <span className="font-medium text-slate-900">{formatCurrency(cost)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Project Summary */}
        <div className="bg-white rounded-lg shadow-xl shadow-stone-200/50 p-8 mb-8 animate-fade-in-up animation-delay-300">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Project Summary</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-stone-100">
                <span className="text-stone-600">Project Type</span>
                <span className="font-semibold text-slate-900">{projectInfo?.name}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-stone-100">
                <span className="text-stone-600">Size</span>
                <span className="font-semibold text-slate-900">{formatSize(estimate.room_size)}</span>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-stone-100">
                <span className="text-stone-600">Quality Level</span>
                <span className="font-semibold text-slate-900">{formatQuality(estimate.quality_level)}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-stone-100">
                <span className="text-stone-600">Complexity</span>
                <span className="font-semibold text-slate-900">{formatComplexity(estimate.complexity)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8 animate-fade-in-up animation-delay-400">
          <p className="text-sm text-amber-800">
            <strong>Please Note:</strong> This estimate is based on typical UK market rates and your selections. 
            The actual cost may vary based on site conditions, access requirements, and specific design details. 
            Request a detailed quote for an accurate assessment.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-500">
          <Button
            variant="outline"
            onClick={() => navigate(`/estimator/${estimate.project_type}`)}
            className="h-12 px-6"
            data-testid="modify-estimate-button"
          >
            <ArrowLeft className="mr-2" size={18} />
            Modify Estimate
          </Button>
          
          <Button
            onClick={handleRequestQuote}
            className="flex-1 bg-amber-700 text-white hover:bg-amber-600 h-14 px-8 font-bold shadow-lg shadow-amber-900/20 transition-all hover:-translate-y-0.5 text-base"
            data-testid="request-quote-button"
          >
            Request Detailed Quote
            <ArrowRight className="ml-2" size={20} />
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ResultsPage;
